import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order, OrderStatus } from '@reims-local/types';
import { orderApi } from '../lib/api';

export function Orders() {
  const [rows, setRows] = useState<Array<{ order: Order; buyer: { id: string; name: string | null; email: string; phone: string | null } }>>([]);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await orderApi.list(statusFilter || undefined);
      setRows(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const intv = setInterval(() => void load(), 30_000);
    return () => clearInterval(intv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Commandes ({rows.length})</h2>
        <div className="spacer" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as OrderStatus | '')}>
          <option value="">Tous statuts</option>
          <option value="confirmed">Confirmées</option>
          <option value="preparing">En préparation</option>
          <option value="ready">Prêtes</option>
          <option value="picked_up">Récupérées</option>
          <option value="cancelled">Annulées</option>
        </select>
        <button onClick={() => void load()}>Rafraîchir</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : rows.length === 0 ? (
        <div className="card"><p>Aucune commande pour l’instant.</p></div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>N°</th>
              <th>Retrait</th>
              <th>Client</th>
              <th>Total</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ order, buyer }) => (
              <tr key={order.id}>
                <td><Link to={`/orders/${order.id}`}>{order.order_number}</Link></td>
                <td>{formatPickup(order.pickup_slot)}</td>
                <td>
                  <div>{buyer.name ?? buyer.email}</div>
                  <div className="muted">{buyer.phone ?? '—'}</div>
                </td>
                <td>{(order.total_cents / 100).toFixed(2)} €</td>
                <td><span className={`badge ${statusClass(order.status as OrderStatus)}`}>{statusLabel(order.status as OrderStatus)}</span></td>
                <td><Link to={`/orders/${order.id}`}><button>Ouvrir</button></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function OrderDetail({ orderId }: { orderId: string }) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof orderApi.get>>['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelInput, setShowCancelInput] = useState(false);

  async function load() {
    try {
      const res = await orderApi.get(orderId);
      setDetail(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [orderId]);

  async function advance(next: OrderStatus) {
    setUpdating(true);
    try {
      await orderApi.patch(orderId, next, next === 'cancelled' ? cancelReason : undefined);
      await load();
      setShowCancelInput(false);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setUpdating(false);
    }
  }

  if (loading) return <p>Chargement…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!detail) return <p>Introuvable.</p>;

  const { order, items, buyer } = detail;
  const status = order.status as OrderStatus;
  const nextActions = getNextActions(status);

  return (
    <>
      <Link to="/orders">← Retour aux commandes</Link>
      <h2 style={{ marginTop: 12 }}>Commande {order.order_number}</h2>

      <div className="card">
        <div className="grid-2">
          <div>
            <div className="muted">Statut</div>
            <div><span className={`badge ${statusClass(status)}`}>{statusLabel(status)}</span></div>
          </div>
          <div>
            <div className="muted">Retrait</div>
            <div><strong>{formatPickup(order.pickup_slot)}</strong></div>
          </div>
          <div>
            <div className="muted">Client</div>
            <div>{buyer?.name ?? buyer?.email ?? '—'}</div>
            <div className="muted">{buyer?.phone ?? buyer?.email}</div>
          </div>
          <div>
            <div className="muted">Total</div>
            <div><strong>{(order.total_cents / 100).toFixed(2)} €</strong></div>
          </div>
        </div>
        {order.notes && <><div className="muted" style={{ marginTop: 16 }}>Note du client</div><div>{order.notes}</div></>}
        {order.cancel_reason && <><div className="muted" style={{ marginTop: 16 }}>Motif d’annulation</div><div>{order.cancel_reason}</div></>}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Articles</h3>
        <table>
          <thead><tr><th></th><th>Produit</th><th>Qté</th><th>Prix unitaire</th><th>Ligne</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td><div className="product-photo-thumb" style={{ backgroundImage: it.product?.photo_url ? `url(${it.product.photo_url})` : undefined }} /></td>
                <td>{it.product?.name ?? '(supprimé)'}</td>
                <td>{it.quantity}</td>
                <td>{(it.unit_price_cents / 100).toFixed(2)} €</td>
                <td><strong>{((it.unit_price_cents * it.quantity) / 100).toFixed(2)} €</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {nextActions.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Actions</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {nextActions.map(({ next, label, primary }) => (
              <button
                key={next}
                className={primary ? 'primary' : next === 'cancelled' ? 'danger' : ''}
                disabled={updating}
                onClick={() => {
                  if (next === 'cancelled') { setShowCancelInput(true); return; }
                  void advance(next);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          {showCancelInput && (
            <div style={{ marginTop: 16 }}>
              <div className="form-row">
                <label>Motif d’annulation (visible par le client)</label>
                <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
              </div>
              <button className="danger" onClick={() => advance('cancelled')} disabled={updating}>Confirmer l’annulation</button>{' '}
              <button onClick={() => setShowCancelInput(false)}>Annuler</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function getNextActions(s: OrderStatus): Array<{ next: OrderStatus; label: string; primary?: boolean }> {
  switch (s) {
    case 'confirmed': return [
      { next: 'preparing', label: 'Démarrer la préparation', primary: true },
      { next: 'cancelled', label: 'Annuler' },
    ];
    case 'preparing': return [
      { next: 'ready', label: 'Marquer comme prêt (client notifié)', primary: true },
      { next: 'cancelled', label: 'Annuler' },
    ];
    case 'ready': return [
      { next: 'picked_up', label: 'Marquer comme récupéré', primary: true },
      { next: 'cancelled', label: 'Annuler' },
    ];
    default: return [];
  }
}

function statusClass(s: OrderStatus): string {
  if (s === 'picked_up') return 'green';
  if (s === 'cancelled') return 'red';
  if (s === 'ready') return 'blue';
  return 'orange';
}

function statusLabel(s: OrderStatus): string {
  const m: Record<OrderStatus, string> = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'En préparation',
    ready: 'Prête',
    picked_up: 'Récupérée',
    cancelled: 'Annulée',
  };
  return m[s];
}

function formatPickup(iso: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
}
